/**
 * Manager Performance Service — Real API Adapters
 *
 * Connected endpoints:
 *   GET /api/manager/kpis          → consolidated KPIs (availability, club_load, adherence, top_performers, active_alerts)
 *   GET /api/manager/active-alerts → alerts array
 *   GET /api/gestor/methodology/active → active methodology object
 *   GET /api/roster/categories     → club categories
 *
 * Pending endpoints (explicit fallback):
 *   GET /api/manager/categories-summary → categories with athlete_count + readiness (NOT YET CREATED)
 *   GET /api/manager/weekly-trends      → weekly readiness per category (NOT YET CREATED)
 */
import { AZURE } from './api';

// ─── Real API Adapters ───────────────────────────────────────────────────────

/**
 * Categories summary with athlete count and readiness.
 * ⚠️ PENDING: No dedicated endpoint yet. Uses /roster/categories as partial source.
 *
 * @param {number} institutionId - ID do clube ativo. OBRIGATÓRIO desde jul/2026.
 *   TODO(SEC-CONFLATION): parâmetro semanticamente é "institution_id" mas hoje o frontend
 *   passa club_id (idênticos no MVP com 3 tenants). Quebra no 4º cliente ou primeiro com
 *   club_id ≠ institution_id. Ver SECURITY_BACKLOG.md → SEC-CONFLATION.
 *   Default 1 removido: derivava silenciosamente para VUKZ (tenant errado) quando o caller
 *   esquecia o parâmetro — mesmo padrão do fix de getGestorDashboard (jun/2026).
 * @returns {Promise<Array<{id, name, athlete_count, readiness_avg}>>}
 */
export const getCategoriesSummary = async (institutionId) => {
  if (!institutionId) {
    console.warn('[managerPerformanceService] getCategoriesSummary chamado sem institutionId — abortando (sem derivação para 1).');
    return [];
  }
  try {
    const { data } = await AZURE.get('/manager/categories-summary', { params: { institution_id: institutionId } });
    const categories = data?.data || [];
    return categories.map(c => ({
      id: c.category_id || c.id,
      name: c.category_name || c.name || 'Sem nome',
      age_range: c.min_age && c.max_age ? `${c.min_age}–${c.max_age} anos` : '',
      athlete_count: c.athlete_count || 0,
      active_count: c.active_count ?? c.athlete_count ?? 0,
      readiness_avg: c.readiness_avg || 0,
      readiness_trend: 'stable',
      readiness_delta: '0.0',
      // View 343: male_count/female_count reais por atleta (fallback: gênero da categoria)
      gender_split: {
        m: c.male_count ?? (c.gender === 'male' ? (c.athlete_count || 0) : 0),
        f: c.female_count ?? (c.gender === 'female' ? (c.athlete_count || 0) : 0),
      },
      responsible_staff: c.responsible_staff || null,
      weekly_load: { volume_km: 0, intensity_avg: 0, monotony: 0 },
      adherence_pct: 0,
      risk_count: 0,
    }));
  } catch (err) {
    console.warn('[managerPerformanceService] getCategoriesSummary: error', err?.response?.status);
    return [];
  }
};

/**
 * @param {string|number} categoryId
 * @param {number} institutionId - id do clube ativo. OBRIGATÓRIO desde jul/2026.
 * @param {number} planId - id do plano estratégico ativo. OBRIGATÓRIO desde jul/2026.
 *   TODO(SEC-CONFLATION): mesmo caveat de getCategoriesSummary — institutionId recebe
 *   club_id hoje (coincidem no MVP). Ver SECURITY_BACKLOG.md → SEC-CONFLATION.
 * @returns {Promise<null|Object>}
 */
export const getCategoryDetail = async (categoryId, institutionId, planId) => {
  if (!institutionId || !planId) {
    console.warn('[managerPerformanceService] getCategoryDetail chamado sem institutionId/planId — abortando (sem derivação para 1).');
    return null;
  }
  try {
    // Fetch categories-summary + directives and merge by category_id/category_code
    const [catRes, dirRes] = await Promise.all([
      AZURE.get('/manager/categories-summary', { params: { institution_id: institutionId } }),
      AZURE.get(`/gestor/plans/${planId}/directives`),
    ]);
    const categories = catRes.data?.data || [];
    const directives = dirRes.data?.data || [];

    // Match by numeric id or string code
    const cat = categories.find(c =>
      String(c.category_id) === String(categoryId) || c.category_code === categoryId
    );
    const dir = directives.find(d =>
      d.category_id === categoryId || d.category_id === cat?.category_code
    );

    if (!cat && !dir) return null;

    return {
      id: cat?.category_id || categoryId,
      name: dir?.category_name || cat?.category_name || categoryId,
      age_range: dir?.age_range || `${cat?.min_age || '?'}–${cat?.max_age || '?'} anos`,
      athlete_count: cat?.athlete_count || 0,
      goals: dir?.focus_technical || 'Sem objetivo definido',
      readiness_avg: cat?.readiness_avg || 0,
      weekly_load: { volume_km: dir?.weekly_load_min_km || 0, monotony: 0 },
      adherence_pct: 0,
      risk_count: 0,
      focus_technical: dir?.focus_technical || '',
      focus_physical: dir?.focus_physical || '',
      intensity_distribution: dir?.intensity_distribution || {},
      restrictions: dir?.restrictions || [],
      priority_objectives: dir?.priority_objectives || [],
      weekly_load_min_km: dir?.weekly_load_min_km || 0,
      weekly_load_max_km: dir?.weekly_load_max_km || 0,
      methodology_divergences: [],
      weekly_history: [],
      athletes: [],
    };
  } catch (err) {
    console.warn('[managerPerformanceService] getCategoryDetail: error', err?.response?.status);
    return null;
  }
};

/**
 * Active methodology from gestor domain.
 * endpoint: GET /api/gestor/methodology/active
 * @returns {Promise<null|Object>}
 */
export const getMethodology = async () => {
  try {
    const { data } = await AZURE.get('/gestor/methodology/active');
    return data?.data || null;
  } catch (err) {
    if (err?.response?.status === 404) {
      console.warn('[managerPerformanceService] getMethodology: no active methodology');
      return null;
    }
    console.error('[managerPerformanceService] getMethodology error:', err?.response?.status);
    throw err;
  }
};

/** @returns {Promise<Array>} */
export const getInterventions = async (_status = 'all') => {
  // Not used in ManagerHomePage — keep as stub
  console.warn('[managerPerformanceService] getInterventions: not yet implemented');
  return [];
};

/**
 * Active alerts from manager KPIs.
 * endpoint: GET /api/manager/kpis → data.active_alerts
 * @returns {Promise<Array<{id, severity, category, message, resolved}>>}
 */
export const getAlerts = async () => {
  try {
    const { data } = await AZURE.get('/manager/kpis');
    const alerts = data?.data?.active_alerts || [];
    // Normalize to expected shape
    return alerts.map((a, idx) => ({
      id: a.id || idx,
      severity: a.severity || 'info',
      category: a.alert_type || a.category || 'Sistema',
      message: a.message || a.alert_type || 'Alerta',
      resolved: a.resolved || false,
    }));
  } catch (err) {
    if (err?.response?.status === 401 || err?.response?.status === 403) throw err;
    console.warn('[managerPerformanceService] getAlerts: error', err?.response?.status);
    return [];
  }
};

/**
 * Weekly trends per category.
 * ⚠️ PENDING: No endpoint exists yet. Returns empty with explicit warning.
 * @returns {Promise<Array<{week, infantil, juvenil, junior, senior, master, load_total}>>}
 */
export const getTrends = async () => {
  try {
    const { data } = await AZURE.get('/manager/weekly-trends');
    return data?.data || [];
  } catch (err) {
    console.warn('[managerPerformanceService] getTrends: error', err?.response?.status);
    return [];
  }
};

/**
 * Consolidated manager KPIs (availability, club_load, adherence, top_performers).
 * endpoint: GET /api/manager/kpis
 * @returns {Promise<Object|null>}
 */
export const getManagerKpis = async () => {
  try {
    const { data } = await AZURE.get('/manager/kpis');
    return data?.data || null;
  } catch (err) {
    if (err?.response?.status === 401 || err?.response?.status === 403) throw err;
    console.warn('[managerPerformanceService] getManagerKpis: error', err?.response?.status);
    return null;
  }
};

/**
 * Lista atletas de uma categoria específica.
 * endpoint: GET /api/roster/athletes?institution_id=X&category_id=Y
 * Defesa SEC-001: backend valida institution_id via _validate_institution_membership.
 * Navegação: widget linka para /manager/roster/athlete/:athleteId (AthleteProfilePage,
 *   endpoints /roster/* seguros). NÃO linkar para /coach/athlete/:userId — SEC-A.
 * @param {number} institutionId
 * @param {number|string} categoryId
 * @returns {Promise<Array>}
 */
export const getAthletesByCategory = async (institutionId, categoryId) => {
  // Fail-closed: categoryId não-numérico (ex: category_code string) viraria None no
  // backend (type=int) e o SP retornaria TODOS os atletas do clube sem filtro.
  const catNum = Number(categoryId);
  if (!institutionId || !Number.isFinite(catNum) || catNum <= 0) return [];
  try {
    const { data } = await AZURE.get('/roster/athletes', {
      params: { institution_id: institutionId, category_id: catNum },
    });
    return data?.data || [];
  } catch (err) {
    console.warn('[managerPerformanceService] getAthletesByCategory: error', err?.response?.status);
    return [];
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const TAG_LABELS = {
  carga: 'Carga',
  metodologia: 'Metodologia',
  retorno: 'Retorno',
  nutricao: 'Nutrição',
  observacao: 'Observação',
};

export const TAG_COLORS = {
  carga: { bg: '#dbeafe', text: '#1e40af' },
  metodologia: { bg: '#fef3c7', text: '#92400e' },
  retorno: { bg: '#fce7f3', text: '#9d174d' },
  nutricao: { bg: '#d1fae5', text: '#065f46' },
  observacao: { bg: '#f3f4f6', text: '#374151' },
};

export const STATUS_LABELS = {
  pending: 'Pendente',
  in_progress: 'Em Execução',
  completed: 'Concluída',
};

export const STATUS_COLORS = {
  pending: { bg: '#fef3c7', text: '#92400e' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  completed: { bg: '#d1fae5', text: '#065f46' },
};

export const SEVERITY_COLORS = {
  critical: { bg: '#fee2e2', text: '#991b1b', dot: '#dc2626' },
  warning: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  info: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
};
